import { StoreModelCollection } from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { StoreModelNames } from "../../dali/type/dali.type";
import { ClassificationStoreModel } from "../../../models/store/classification.store.model";
import { computed, signal } from "@angular/core";
import { ClassificationView } from "@bitwarden/web-vault/app/models/view/classification/classification.view";
import { type IModelView } from "@bitwarden/web-vault/app/models/view/model.view";
import {
  DEFAULT_CLASSIFICATION_ID,
  DEFAULT_CLASSIFICATION_NAME,
} from "@bitwarden/web-vault/app/models/constants/classification.constants";
import { ClassificationUtils } from "@bitwarden/web-vault/app/models/view/classification/classification.utils";
import { StoreModel } from "@bitwarden/web-vault/app/services/dali/vault-parser/parser.type";

export class ClassificationsStoreCollection extends StoreModelCollection {
  readonly type: StoreModelNames = "ClassificationStoreModel";

  protected models: Map<string, ClassificationStoreModel> = new Map<
    string,
    ClassificationStoreModel
  >();

  private _classificationViews = signal<ClassificationView[]>(null);
  readonly classificationViews = this._classificationViews.asReadonly();

  classificationViewsSorted = computed(() =>
    [...(this.classificationViews() ?? [])].sort((a, b) => a.name?.localeCompare(b.name)),
  );

  async initialize(): Promise<void> {
    if (this.models.size === 0) {
      await this.createDefaultClassification();
    }
  }

  /**
   * Need to double-check that we don't save over the name of the default classification if it is changed
   * @param view
   */
  override async save(
    view: IModelView,
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<StoreModel> {
    // This collection only ever receives ClassificationView instances; the base signature is
    // IModelView so the override stays sound for callers holding a StoreModelCollection ref.
    const classificationView = view as ClassificationView;
    if (classificationView.id === DEFAULT_CLASSIFICATION_ID) {
      classificationView.name = DEFAULT_CLASSIFICATION_NAME;
      classificationView.generatedDefault = true;
    }
    return await super.save(classificationView, organizationId, collectionIds);
  }

  /**
   * Need to ensure we don't delete the default classification
   */
  override async delete(view: IModelView): Promise<boolean> {
    if (view.id !== DEFAULT_CLASSIFICATION_ID) {
      if (!this.classificationInUse(view.id)) {
        return await super.delete(view);
      }
    }
  }

  /**
   * Need to make sure we don't change the name of the default classification if it is changed
   */
  override async saveToVault(
    views: IModelView[],
    triggerChanges = false,
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<boolean> {
    // This collection only ever receives ClassificationView instances; the base signature is
    // IModelView so the override stays sound for callers holding a StoreModelCollection ref.
    const classificationViews = [...(views as ClassificationView[])];
    const defaultView = classificationViews.find((view) => view.id === DEFAULT_CLASSIFICATION_ID);
    if (defaultView) {
      defaultView.name = DEFAULT_CLASSIFICATION_NAME;
      defaultView.generatedDefault = true;
    } else if (!this.models.has(DEFAULT_CLASSIFICATION_ID)) {
      // Ensure the default classification is always included
      classificationViews.push(ClassificationUtils.getDefaultClassificationView());
    }
    return await super.saveToVault(
      classificationViews,
      triggerChanges,
      organizationId,
      collectionIds,
    );
  }

  /**
   * Need to ensure we don't delete the default classification
   */
  override async deleteFromVault(views: IModelView[]): Promise<boolean> {
    // This collection only ever receives ClassificationView instances; the base signature is
    // IModelView so the override stays sound for callers holding a StoreModelCollection ref.
    const classificationViews = views as ClassificationView[];
    classificationViews.forEach((view) => {
      if (this.classificationInUse(view.id)) {
        this.log.error(`Classification ${view.name} is in use and cannot be deleted`);
        throw new Error(`Classification ${view.name} is in use and cannot be deleted`);
      }
    });

    const filteredViews = classificationViews.filter(
      (view) => view.id !== DEFAULT_CLASSIFICATION_ID,
    );
    return await super.deleteFromVault(filteredViews);
  }

  triggerChanges(): void {
    const views: ClassificationView[] = [];

    this.models.forEach((model) => {
      views.push(this.deepFreeze(new ClassificationView(model)));
    });
    this._classificationViews.set(views);
  }

  clear(): void {
    this.models.clear();
    this._classificationViews.set(null);
  }

  async createDefaultClassification() {
    // Ensure there is always at least the default classification
    const defaultClassification = ClassificationUtils.getDefaultClassificationView();
    try {
      const savedDefault = await this.save(defaultClassification);
      if (!savedDefault) {
        this.log.error("Failed to save default classification");
      }
    } catch (e) {
      this.log.error("Error creating default classification");
      this.log.error(e.message);
    }
  }

  /**
   * TODO: check that there are no transactions that are using this classification
   * and no accounts where this is the default classification.
   * We may not need this if we implement this on the UI side.
   * @param classificationId
   * @return boolean - returns true if the classification is in use and false if not
   */
  classificationInUse(classificationId: string): boolean {
    // TODO: write code to check the transactions and accounts store collections
    return false;
  }
}
